import React, { useState, useCallback } from 'react';
import CardGrid from '../components/Cards/CardGrid';
import ScrapeButton from '../components/Scraper/ScrapeButton';
import AddByUrlForm from '../components/Scraper/AddByUrlForm';
import { useCards } from '../hooks/useCards';
import { useScraper } from '../hooks/useScraper';
import { useAuth } from '../context/AuthContext';
import { cardApi } from '../api/client';

export default function DashboardPage() {
  const { user } = useAuth();
  const cardState = useCards();
  const { scraping, status, error: scrapeError, startScrape, addByUrl } = useScraper(cardState.refresh);

  const isAdmin = user?.is_admin || user?.is_super_admin;

  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [backfillError, setBackfillError] = useState(null);

  const [autoLinking, setAutoLinking] = useState(false);
  const [autoLinkResult, setAutoLinkResult] = useState(null);
  const [autoLinkError, setAutoLinkError] = useState(null);

  const handleDeleteUnlinked = useCallback(async () => {
    if (!window.confirm('Are you sure you want to delete ALL unlinked cards? This cannot be undone.')) {
      return;
    }
    setDeleting(true);
    setDeleteResult(null);
    setDeleteError(null);
    try {
      const resp = await cardApi.deleteUnlinked();
      setDeleteResult(resp.data.detail);
      cardState.refresh();
    } catch (err) {
      setDeleteError(err.response?.data?.detail || 'Failed to delete unlinked cards');
    } finally {
      setDeleting(false);
    }
  }, [cardState]);

  const handleBackfillMetadata = useCallback(async () => {
    setBackfilling(true);
    setBackfillResult(null);
    setBackfillError(null);
    try {
      const resp = await cardApi.backfillMetadata();
      setBackfillResult(resp.data.detail);
      cardState.refresh();
    } catch (err) {
      setBackfillError(err.response?.data?.detail || 'Failed to backfill metadata');
    } finally {
      setBackfilling(false);
    }
  }, [cardState]);

  const handleAutoLink = useCallback(async () => {
    setAutoLinking(true);
    setAutoLinkResult(null);
    setAutoLinkError(null);
    try {
      const resp = await cardApi.autoLink();
      setAutoLinkResult(resp.data.detail);
      cardState.refresh();
    } catch (err) {
      setAutoLinkError(err.response?.data?.detail || 'Failed to auto-link cards');
    } finally {
      setAutoLinking(false);
    }
  }, [cardState]);

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <h2>Pokemon Cards</h2>
        <div className="dashboard-actions">
          {isAdmin && (
            <ScrapeButton
              onScrape={startScrape}
              scraping={scraping}
              status={status}
            />
          )}
          {isAdmin && (
            <button
              className="btn"
              onClick={handleBackfillMetadata}
              disabled={backfilling || scraping}
              style={{ marginLeft: '8px' }}
            >
              {backfilling ? 'Backfilling...' : 'Backfill Metadata'}
            </button>
          )}
          {isAdmin && (
            <button
              className="btn"
              onClick={handleAutoLink}
              disabled={autoLinking || scraping}
              style={{ marginLeft: '8px' }}
            >
              {autoLinking ? 'Auto-Linking...' : 'Auto-Link Cards'}
            </button>
          )}
          {isAdmin && (
            <button
              className="btn btn-danger"
              onClick={handleDeleteUnlinked}
              disabled={deleting || scraping}
              style={{ marginLeft: '8px' }}
            >
              {deleting ? 'Deleting...' : 'Delete Unlinked Cards'}
            </button>
          )}
        </div>
      </div>

      {scrapeError && <div className="error-message">{scrapeError}</div>}
      {deleteError && <div className="error-message">{deleteError}</div>}
      {deleteResult && <div className="success-message">{deleteResult}</div>}
      {backfillError && <div className="error-message">{backfillError}</div>}
      {backfillResult && <div className="success-message">{backfillResult}</div>}
      {autoLinkError && <div className="error-message">{autoLinkError}</div>}
      {autoLinkResult && <div className="success-message">{autoLinkResult}</div>}

      {isAdmin && <AddByUrlForm onAddUrl={addByUrl} />}

      <CardGrid {...cardState} />
    </div>
  );
}
